import { Field as BaseField } from 'formik';
import { GraphQLError } from 'graphql';
import { PropsWithChildren, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { NoteFragment, User } from '@treams/treams-network';
import { FlexGap, Pill, ShareButton, Title, Toolbar, Tooltip } from '@treams/treams-ui';

import microsoftTeamsIcon from '../assets/microsoft-teams-icon.svg?url';
import outlookIcon from '../assets/outlook-icon.svg?url';
import { useShareNote } from '../hooks/useShareNote';
import SubjectInput from './SubjectInput';

export function NoteShareButton({
	note,
	showIndicator,
	showTrigger,
}: {
	note: NoteFragment;
	showIndicator?: boolean;
	showTrigger?: boolean;
}) {
	const { t } = useTranslation();
	const { shareNote, unshareNote, error } = useShareNote(note);

	const collaborators = useMemo(
		() => note.collaborators.edges.map((edge) => edge.node) as User[],
		[note.collaborators],
	);

	return (
		<ShareButton
			owner={note.owner as User}
			subject={note.subject as User}
			collaborators={collaborators}
			shareMutation={shareNote}
			unshareMutation={unshareNote}
			entityType={note.subject ? 'oneOnOne' : 'note'}
			buttonProps={{ type: 'primary' }}
			canShare={note.viewerCanShare}
			showIndicator={showIndicator}
			showTrigger={showTrigger}
			shareModalProps={{
				ownerSubtitle: t('oneOnOne.meta.ownerLabel'),
				subjectSubtitle: t('oneOnOne.meta.subjectLabel'),
				mutationError: error as unknown as GraphQLError,
				suggestManagers: true,
			}}
		/>
	);
}

const Field = styled(BaseField)`
	field-sizing: content;
	max-width: 100%;
	text-overflow: ellipsis;
`;

const TitleField = Title.withComponent(Field);

export default function NoteMetaFormFields({ note }: { note: NoteFragment }) {
	const { t } = useTranslation();

	const titlePlaceholder = note.subject
		? t('oneOnOne.index.card.titleWithSubject', {
				subject: note.subject.firstName,
			})
		: t('note.index.card.title');

	return (
		<Box>
			<Toolbar mt={4} mb={3} alignItems="center" flexWrap="wrap">
				<NotEditableTooltip note={note}>
					<TitleField
						type="text"
						placeholder={titlePlaceholder}
						name="title"
						autoComplete="off"
						disabled={!note.viewerCanUpdate}
					/>
				</NotEditableTooltip>
				<Toolbar.Spacing />
				<NoteShareButton note={note} />
			</Toolbar>
			<FlexGap gap={4} alignItems="center">
				<SubjectInput note={note} />
				{note.metadata?.source === 'OUTLOOK' && (
					<Pill
						as="a"
						href={
							note.metadata.outlookItemId
								? `https://outlook.office365.com/owa/?ItemID=${encodeURIComponent(note.metadata.outlookItemId)}&exvsurl=1&viewmodel=ReadMessageItem`
								: 'https://outlook.office.com/mail'
						}
						target="_blank"
						rel="noopener noreferrer"
						small
						style={{
							textDecoration: 'none',
							cursor: 'pointer',
						}}
					>
						<FlexGap alignItems="center" gap={6} px={1}>
							<img src={outlookIcon} alt="Outlook" style={{ width: 14, height: 14 }} />
							{t('common.email')}
						</FlexGap>
					</Pill>
				)}
				{note.metadata?.source === 'MSTEAMS' && (
					<Pill
						as="a"
						href={
							note.metadata.teamsChatId && note.metadata.teamsMessageId
								? `https://teams.microsoft.com/l/message/${encodeURIComponent(note.metadata.teamsChatId)}/${encodeURIComponent(note.metadata.teamsMessageId)}${note.metadata.tenantId ? `?tenantId=${encodeURIComponent(note.metadata.tenantId)}` : ''}`
								: 'https://teams.microsoft.com'
						}
						target="_blank"
						rel="noopener noreferrer"
						small
						style={{
							textDecoration: 'none',
							cursor: 'pointer',
						}}
					>
						<FlexGap alignItems="center" gap={6} px={1}>
							<img
								src={microsoftTeamsIcon}
								alt="Microsoft Teams"
								style={{ width: 14, height: 14 }}
							/>
							{t('microsoft.teams.app.name')}
						</FlexGap>
					</Pill>
				)}
			</FlexGap>
		</Box>
	);
}

function NotEditableTooltip({ note, children }: PropsWithChildren<{ note: NoteFragment }>) {
	const { t } = useTranslation();
	const content = t('notes.collaborators.sharedIndicationText', {
		ownerFirstName: note.owner.firstName,
	});

	return (
		<Tooltip content={content} enabled={!note.viewerCanUpdate} position="bottom">
			{children}
		</Tooltip>
	);
}
